import { Box, CircularProgress, Tooltip } from "@mui/material";
import { FC, memo } from "react";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import styles from "./NewTickerTape.module.scss";
import { getTimeDifference } from "@/lib/utils";
import InfoIcon from "@mui/icons-material/Info";
import dayjs from "dayjs";
import { Instrument } from "@/models/instruments";
import { TickerFeedStore, useTickerInstrument } from "src/hooks/useTickerTapeFeed";

interface PropsTypes {
    store: TickerFeedStore;
    tickerKey: string;
    handleTickerClick: (e: React.MouseEvent, instrument: [string, Instrument]) => void;
}

const elementSx = { width: "max-content", backgroundColor: "transparent" } as const;
const spinnerWrapSx = { display: "flex", alignItems: "center", justifyContent: "center", minWidth: "54px" } as const;
const arrowSx = { fontSize: "14px" } as const;
const infoSx = { fontSize: "14px", marginLeft: 0.5 } as const;
const percentTextStyle = { display: "flex", alignItems: "center", fontWeight: "normal" } as const;

const formatPercentage = (data: Instrument): string => {
    if (typeof data.percentage === "number" && Number.isFinite(data.percentage)) {
        let rounded = data.percentage.toFixed(2);
        if (rounded === "-0.00") rounded = "0.00";
        return rounded + "%";
    }
    if (typeof data.lastChange === "number" && typeof data.lastPrice === "number" && data.lastPrice !== 0) {
        const percent = (data.lastChange / data.lastPrice) * 100;
        if (!Number.isFinite(percent)) return "N/A";
        let rounded = percent.toFixed(2);
        if (rounded === "-0.00") rounded = "0.00";
        return rounded + "%";
    }
    return "N/A";
};

const TickerStock: FC<PropsTypes> = ({ store, tickerKey, handleTickerClick }) => {
    const { data, isLoading } = useTickerInstrument(store, tickerKey);

    if (tickerKey === undefined || !data.name) return null;

    const percentage = formatPercentage(data);
    const priceChange = percentage.includes("-") ? "negative" : percentage === "N/A" || percentage === "0.00%" ? "grey" : "positive";

    const name = `${data.name.slice(0, data.name.length > 15 ? 15 : data.name.length)}${data.name.length > 15 ? "..." : ""}`;
    const displayPrice = (data.price ?? data.lastPrice)?.toFixed(2);

    const timeDiff = getTimeDifference(data.timestamp);
    const showStaleInfo = timeDiff[1] < 0 || timeDiff[1] > 20 || timeDiff[0] !== 0;

    return (
        <Box className={styles.tickertapeElement} sx={elementSx} onClick={(e) => handleTickerClick(e, [tickerKey, data])}>
            <Box className={styles.slideItem}>
                <p>
                    {name}
                    &nbsp;
                </p>
                <p>{displayPrice}</p>
                <div className={styles.priceVariation}>
                    {isLoading ? (
                        <Box sx={spinnerWrapSx}>
                            <CircularProgress size={15} />
                        </Box>
                    ) : (
                        <>
                            {priceChange === "negative" ? <ArrowDownwardIcon className="negative" sx={arrowSx} /> : priceChange === "positive" ? <ArrowUpwardIcon className="positive" sx={arrowSx} /> : null}
                            <p className={priceChange} style={percentTextStyle}>
                                &nbsp;
                                {percentage}
                            </p>
                        </>
                    )}
                    {showStaleInfo && (
                        <Tooltip arrow title={`Last updated: ${dayjs(data.timestamp).format("DD MMM HH:mm")}`}>
                            <InfoIcon sx={infoSx} />
                        </Tooltip>
                    )}
                </div>
            </Box>
        </Box>
    );
};

export default memo(TickerStock);
